import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
	SiteActionTypes,
	GetSite,
	GetSiteSuccess,
	GetSiteFailure,
	GetSiteNames,
	GetSiteNamesSuccess,
	GetSiteNamesFailure,
	GetLeakFormDetailsForSite,
	GetLeakFormDetailsForSiteSuccess,
	GetLeakFormDetailsForSiteFailure,
	GetSteamCosts,
	GetSteamCostsSuccess,
	GetSteamCostsFailure,
	UpdateSteamCostsSuccess,
	UpdateSteamCostsFailure,
	UpdateSteamCosts,
	AddFitting,
	AddFittingSuccess,
	UpdateFitting,
	UpdateFittingSuccess,
	AddFittingFailure,
	UpdateFittingFailure,
	DeleteFitting,
	DeleteFittingSuccess,
	DeleteFittingFailure,
	AddFolder,
	AddFolderSuccess,
	UpdateFolder,
	UpdateFolderSuccess,
	AddFolderFailure,
	UpdateFolderFailure,
	DeleteFolder,
	DeleteFolderSuccess,
	DeleteFolderFailure,
	AddHearingProtectionType,
	AddHearingProtectionTypeSuccess,
	AddHearingProtectionTypeFailure,
	UpdateHearingProtectionType,
	UpdateHearingProtectionTypeSuccess,
	UpdateHearingProtectionTypeFailure,
	DeleteHearingProtectionType,
	DeleteHearingProtectionTypeSuccess,
	DeleteHearingProtectionTypeFailure,
	AddInsulationType,
	AddInsulationTypeSuccess,
	AddInsulationTypeFailure,
	UpdateInsulationType,
	UpdateInsulationTypeSuccess,
	DeleteInsulationType,
	DeleteInsulationTypeSuccess,
	DeleteInsulationTypeFailure,
	AddIsoNotVerifiedReason,
	AddIsoNotVerifiedReasonSuccess,
	AddIsoNotVerifiedReasonFailure,
	UpdateIsoNotVerifiedReason,
	UpdateIsoNotVerifiedReasonSuccess,
	UpdateIsoNotVerifiedReasonFailure,
	UpdateInsulationTypeFailure,
	DeleteIsoNotVerifiedReason,
	DeleteIsoNotVerifiedReasonSuccess,
	DeleteIsoNotVerifiedReasonFailure,
	AddStatus,
	AddStatusSuccess,
	AddStatusFailure,
	UpdateStatus,
	UpdateStatusSuccess,
	UpdateStatusFailure,
	DeleteStatus,
	DeleteStatusSuccess,
	DeleteStatusFailure,
	AddTeam,
	AddTeamFailure,
	AddTeamSuccess,
	DeleteTeam,
	DeleteTeamFailure,
	DeleteTeamSuccess,
	UpdateTeam,
	UpdateTeamFailure,
	UpdateTeamSuccess,
	AddVendor,
	AddVendorFailure,
	AddVendorSuccess,
	DeleteVendor,
	DeleteVendorFailure,
	DeleteVendorSuccess,
	UpdateVendor,
	UpdateVendorFailure,
	UpdateVendorSuccess,
} from '../actions/site.actions';
import { SiteService } from '../../site.service';
import {
	Site,
	LeakFormDetails,
	SteamCost,
	SiteName,
	Fitting,
	Folder,
	HearingProtectionType,
	InsulationType,
	IsolationNotVerifiedReason,
	LeakStatus,
	Team,
	Vendor,
} from '../../site.interfaces';
import { ToastService } from 'src/app/toast/toast.service';

@Injectable()
export class SiteEffects {
	loadSite$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<GetSite>(SiteActionTypes.GetSite),
		map(action => action.siteId),
		switchMap(siteId => {
			return this.siteService.getSite(siteId).pipe(
				switchMap((site: Site) => {
					if (site.boundingPolygons && site.boundingPolygons.length > 0) {
						site.regions = site.boundingPolygons.map(polygon => {
							return this.siteService.formatBoundingPolygon(polygon.points);
						});
					} else {
						site.regions = [];
					}
					return of(new GetSiteSuccess(site));
				}),
				catchError(error => of(new GetSiteFailure(error)))
			);
		})
		)
	);

	getSiteNames$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<GetSiteNames>(SiteActionTypes.GetSiteNames),
		map(action => action),
		switchMap(_ => {
			return this.siteService.getSiteNames().pipe(
				switchMap((siteNames: SiteName[]) => {
					return of(new GetSiteNamesSuccess(siteNames));
				}),
				catchError(error => of(new GetSiteNamesFailure(error)))
			);
		}))
	);

	getLeakFormDetailsForSite$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<GetLeakFormDetailsForSite>(SiteActionTypes.GetLeakFormDetailsForSite),
		switchMap(action => {
			return this.siteService.getLeakFormData(action.siteId).pipe(
				map((formDetails: LeakFormDetails) => {
					return new GetLeakFormDetailsForSiteSuccess(formDetails);
				}),
				catchError((error, caught) => of(new GetLeakFormDetailsForSiteFailure(error)))
			);
		}))
	);

	getSteamCosts$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<GetSteamCosts>(SiteActionTypes.GetSteamCosts),
		switchMap(action => {
			return this.siteService.getSteamCosts(action.siteId).pipe(
				map((steamCosts: SteamCost[]) => {
					return new GetSteamCostsSuccess(steamCosts);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new GetSteamCostsFailure(error));
				})
			);
		}))
	);

	updateSteamCosts$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateSteamCosts>(SiteActionTypes.UpdateSteamCosts),
		switchMap(action => {
			return this.siteService.updateSteamCosts(action.steamCosts).pipe(
				map((steamCosts: SteamCost[]) => {
					this.toastService.addMessage({ text: `Steam costs updated. Please allow a few minutes for leak costs to update`, error: false });
					return new UpdateSteamCostsSuccess(steamCosts);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: `An error occured while updating the steam costs. Save Failed.`,
						error: true,
					});
					return of(new UpdateSteamCostsFailure(error));
				})
			);
		}))
	);

	addFitting$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddFitting>(SiteActionTypes.AddFitting),
		switchMap(action => {
			return this.siteService.addFitting(action.newFitting).pipe(
				map((fitting: Fitting) => {
					this.toastService.addMessage({ text: `New fitting '${fitting.name}' added successfully.`, error: false });
					return new AddFittingSuccess(fitting);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddFittingFailure(error));
				})
			);
		}))
	);

	updateFitting$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateFitting>(SiteActionTypes.UpdateFitting),
		switchMap(action => {
			return this.siteService.updateFitting(action.editFitting).pipe(
				map((fitting: Fitting) => {
					this.toastService.addMessage({ text: `Fitting '${fitting.name}' updated successfully.`, error: false });
					return new UpdateFittingSuccess(fitting);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateFittingFailure(error));
				})
			);
		}))
	);

	deleteFitting$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeleteFitting>(SiteActionTypes.DeleteFitting),
		switchMap(action => {
			return this.siteService.deleteFitting(action.fittingId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Fitting deleted successfully.`, error: false });
					return new DeleteFittingSuccess(action.fittingId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteFittingFailure(error));
				})
			);
		}))
	);

	addFolder$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddFolder>(SiteActionTypes.AddFolder),
		switchMap(action => {
			return this.siteService.addFolder(action.newFolder).pipe(
				map((folder: Folder) => {
					this.toastService.addMessage({ text: `New folder '${folder.name}' added successfully.`, error: false });
					return new AddFolderSuccess(folder);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddFolderFailure(error));
				})
			);
		}))
	);

	updateFolder$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateFolder>(SiteActionTypes.UpdateFolder),
		switchMap(action => {
			return this.siteService.updateFolder(action.editFolder).pipe(
				map((folder: Folder) => {
					this.toastService.addMessage({ text: `Folder '${folder.name}' updated successfully.`, error: false });
					return new UpdateFolderSuccess(folder);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateFolderFailure(error));
				})
			);
		}))
	);

	deleteFolder$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeleteFolder>(SiteActionTypes.DeleteFolder),
		switchMap(action => {
			return this.siteService.deleteFolder(action.folderId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Folder deleted successfully.`, error: false });
					return new DeleteFolderSuccess(action.folderId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteFolderFailure(error));
				})
			);
		}))
	);

	addHearingProtectionType$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddHearingProtectionType>(SiteActionTypes.AddHearingProtectionType),
		switchMap(action => {
			return this.siteService.addHearingProtectionType(action.newHearingProtectionType).pipe(
				map((hearingProtectionType: HearingProtectionType) => {
					this.toastService.addMessage({ text: `New Hearing Protection Type '${hearingProtectionType.name}' added successfully.`, error: false });
					return new AddHearingProtectionTypeSuccess(hearingProtectionType);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddHearingProtectionTypeFailure(error));
				})
			);
		}))
	);

	updateHearingProtectionType$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateHearingProtectionType>(SiteActionTypes.UpdateHearingProtectionType),
		switchMap(action => {
			return this.siteService.updateHearingProtectionType(action.editHearingProtectionType).pipe(
				map((hearingProtectionType: HearingProtectionType) => {
					this.toastService.addMessage({ text: `Hearing Protection Type '${hearingProtectionType.name}' updated successfully.`, error: false });
					return new UpdateHearingProtectionTypeSuccess(hearingProtectionType);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateHearingProtectionTypeFailure(error));
				})
			);
		}))
	);

	deleteHearingProtectionType$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeleteHearingProtectionType>(SiteActionTypes.DeleteHearingProtectionType),
		switchMap(action => {
			return this.siteService.deleteHearingProtectionType(action.hearingProtectionTypeId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Hearing Protection Type deleted successfully.`, error: false });
					return new DeleteHearingProtectionTypeSuccess(action.hearingProtectionTypeId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteHearingProtectionTypeFailure(error));
				})
			);
		}))
	);

	addInsulationType$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddInsulationType>(SiteActionTypes.AddInsulationType),
		switchMap(action => {
			return this.siteService.addInsulationType(action.newInsulationType).pipe(
				map((insulationType: InsulationType) => {
					this.toastService.addMessage({ text: `New Insulation Type '${insulationType.name}' added successfully.`, error: false });
					return new AddInsulationTypeSuccess(insulationType);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddInsulationTypeFailure(error));
				})
			);
		}))
	);

	updateInsulationType$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateInsulationType>(SiteActionTypes.UpdateInsulationType),
		switchMap(action => {
			return this.siteService.updateInsulationType(action.editInsulationType).pipe(
				map((insulationType: InsulationType) => {
					this.toastService.addMessage({ text: `Insulation Type '${insulationType.name}' updated successfully.`, error: false });
					return new UpdateInsulationTypeSuccess(insulationType);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateInsulationTypeFailure(error));
				})
			);
		}))
	);

	deleteInsulationType$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeleteInsulationType>(SiteActionTypes.DeleteInsulationType),
		switchMap(action => {
			return this.siteService.deleteInsulationType(action.insulationTypeId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Insulation Type deleted successfully.`, error: false });
					return new DeleteInsulationTypeSuccess(action.insulationTypeId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteInsulationTypeFailure(error));
				})
			);
		}))
	);

	addIsoNotVerifiedReason$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddIsoNotVerifiedReason>(SiteActionTypes.AddIsoNotVerifiedReason),
		switchMap(action => {
			return this.siteService.addIsoNotVerifiedReason(action.newIsoNotVerifiedReason).pipe(
				map((isoNotVerifiedReason: IsolationNotVerifiedReason) => {
					this.toastService.addMessage({
						text: `New Isolation Not Verified Reason '${isoNotVerifiedReason.reason}' added successfully.`,
						error: false,
					});
					return new AddIsoNotVerifiedReasonSuccess(isoNotVerifiedReason);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddIsoNotVerifiedReasonFailure(error));
				})
			);
		}))
	);

	updateIsoNotVerifiedReason$ = createEffect(()=>
	this.actions$.pipe(
		ofType<UpdateIsoNotVerifiedReason>(SiteActionTypes.UpdateIsoNotVerifiedReason),
		switchMap(action => {
			return this.siteService.updateIsoNotVerifiedReason(action.editIsoNotVerifiedReason).pipe(
				map((isoNotVerifiedReason: IsolationNotVerifiedReason) => {
					this.toastService.addMessage({
						text: `Isolation Not Verified Reason '${isoNotVerifiedReason.reason}' updated successfully.`,
						error: false,
					});
					return new UpdateIsoNotVerifiedReasonSuccess(isoNotVerifiedReason);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateIsoNotVerifiedReasonFailure(error));
				})
			);
		}))
	);

	deleteIsoNotVerifiedReason$ = createEffect(()=>
	this.actions$.pipe(
		ofType<DeleteIsoNotVerifiedReason>(SiteActionTypes.DeleteIsoNotVerifiedReason),
		switchMap(action => {
			return this.siteService.deleteIsoNotVerifiedReason(action.isoNotVerifiedReasonId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Isolation Not Verified Reason deleted successfully.`, error: false });
					return new DeleteIsoNotVerifiedReasonSuccess(action.isoNotVerifiedReasonId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteIsoNotVerifiedReasonFailure(error));
				})
			);
		}))
	);

	addStatus$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddStatus>(SiteActionTypes.AddStatus),
		switchMap(action => {
			return this.siteService.addStatus(action.newStatus).pipe(
				map((status: LeakStatus) => {
					this.toastService.addMessage({
						text: `New Status '${status.name}' added successfully.`,
						error: false,
					});
					return new AddStatusSuccess(status);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddStatusFailure(error));
				})
			);
		}))
	);

	updateStatus$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateStatus>(SiteActionTypes.UpdateStatus),
		switchMap(action => {
			return this.siteService.updateStatus(action.editStatus).pipe(
				map((status: LeakStatus) => {
					this.toastService.addMessage({
						text: `Status '${status.name}' updated successfully.`,
						error: false,
					});
					return new UpdateStatusSuccess(status);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateStatusFailure(error));
				})
			);
		}))
	);

	deleteStatus$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeleteStatus>(SiteActionTypes.DeleteStatus),
		switchMap(action => {
			return this.siteService.deleteStatus(action.statusId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Status deleted successfully.`, error: false });
					return new DeleteStatusSuccess(action.statusId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteStatusFailure(error));
				})
			);
		}))
	);

	addTeam$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddTeam>(SiteActionTypes.AddTeam),
		switchMap(action => {
			return this.siteService.addTeam(action.newTeam).pipe(
				map((team: Team) => {
					this.toastService.addMessage({
						text: `New Team '${team.name}' added successfully.`,
						error: false,
					});
					return new AddTeamSuccess(team);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddTeamFailure(error));
				})
			);
		}))
	);

	updateTeam$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateTeam>(SiteActionTypes.UpdateTeam),
		switchMap(action => {
			return this.siteService.updateTeam(action.editTeam).pipe(
				map((team: Team) => {
					this.toastService.addMessage({
						text: `Team '${team.name}' updated successfully.`,
						error: false,
					});
					return new UpdateTeamSuccess(team);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateTeamFailure(error));
				})
			);
		}))
	);

	deleteTeam$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeleteTeam>(SiteActionTypes.DeleteTeam),
		switchMap(action => {
			return this.siteService.deleteTeam(action.teamId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Team deleted successfully.`, error: false });
					return new DeleteTeamSuccess(action.teamId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteTeamFailure(error));
				})
			);
		}))
	);

	addVendor$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<AddVendor>(SiteActionTypes.AddVendor),
		switchMap(action => {
			return this.siteService.addVendor(action.newVendor).pipe(
				map((vendor: Vendor) => {
					this.toastService.addMessage({
						text: `New Vendor '${vendor.name}' added successfully.`,
						error: false,
					});
					return new AddVendorSuccess(vendor);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new AddVendorFailure(error));
				})
			);
		}))
	);

	updateVendor$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<UpdateVendor>(SiteActionTypes.UpdateVendor),
		switchMap(action => {
			return this.siteService.updateVendor(action.editVendor).pipe(
				map((vendor: Vendor) => {
					this.toastService.addMessage({
						text: `Vendor '${vendor.name}' updated successfully.`,
						error: false,
					});
					return new UpdateVendorSuccess(vendor);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new UpdateVendorFailure(error));
				})
			);
		}))
	);

	deleteVendor$ = createEffect(()=>
	 this.actions$.pipe(
		ofType<DeleteVendor>(SiteActionTypes.DeleteVendor),
		switchMap(action => {
			return this.siteService.deleteVendor(action.vendorId).pipe(
				map(() => {
					this.toastService.addMessage({ text: `Vendor deleted successfully.`, error: false });
					return new DeleteVendorSuccess(action.vendorId);
				}),
				catchError(error => {
					this.toastService.addMessage({
						text: error,
						error: true,
					});
					return of(new DeleteVendorFailure(error));
				})
			);
		}))
	);

	constructor(private actions$: Actions, private siteService: SiteService, private toastService: ToastService) {}
}
